import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { chartTheme, chartAxisTick } from "@/lib/chartTheme";
import { ChartTooltip } from "@/components/ui/ChartTooltip";

interface EquityDataPoint {
  date: string;
  label: string;
  balance: number;
  equity: number;
  drawdown: number;
}

interface EnhancedEquityCurveProps {
  data: EquityDataPoint[];
  startingBalance: number;
  compact?: boolean;
}

export function EnhancedEquityCurve({ data, startingBalance, compact }: EnhancedEquityCurveProps) {
  const currentBalance = data[data.length - 1]?.balance || startingBalance;
  const currentEquity = data[data.length - 1]?.equity || startingBalance;
  const totalPnL = currentBalance - startingBalance;
  const totalPnLPercent = startingBalance > 0 ? ((totalPnL / startingBalance) * 100).toFixed(2) : null;
  const maxDrawdown = data.length > 0 ? Math.max(...data.map((d) => d.drawdown)) : 0;

  // Balance line: green if positive vs start, red if negative
  const balanceColor = totalPnL >= 0 ? chartTheme.positiveColor : chartTheme.negativeColor;
  // Equity line: slightly dimmer complement
  const equityColor = "rgba(255,255,255,0.35)";

  return (
    <div className={compact ? "relative h-full" : "relative p-5 h-full"} style={{ background: "transparent" }}>
      {!compact && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="equity-shimmer" />
        </div>
      )}
      <div className={compact ? "hidden" : "flex items-center justify-between mb-4"}>
        <div>
          <h3 className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono font-medium">
            Equity Curve
          </h3>
        </div>
        <div className="overflow-x-auto">
          <div className="flex flex-wrap items-center gap-3 sm:gap-6 min-w-max">
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider font-mono" style={{ color: "rgba(255,255,255,0.5)" }}>Balance</p>
              <p className="text-base font-bold font-mono" style={{ color: "rgba(255,255,255,0.92)" }}>
                ${currentBalance.toLocaleString()}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider font-mono" style={{ color: "rgba(255,255,255,0.5)" }}>Equity</p>
              <p className="text-base font-bold font-mono" style={{ color: "rgba(255,255,255,0.92)" }}>
                ${currentEquity.toLocaleString()}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider font-mono" style={{ color: "rgba(255,255,255,0.5)" }}>P/L</p>
              <p className={`text-base font-bold font-mono ${totalPnL >= 0 ? "text-profit" : "text-loss"}`}>
                {totalPnL >= 0 ? "+" : ""}${totalPnL.toLocaleString()} {totalPnLPercent ? `(${totalPnLPercent}%)` : ""}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider font-mono" style={{ color: "rgba(255,255,255,0.5)" }}>Max DD</p>
              <p className="text-base font-bold font-mono text-loss">
                -{maxDrawdown.toFixed(2)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className={compact ? "h-full" : "h-[200px] sm:h-[260px]"}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.gridColor} vertical={false} />
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              minTickGap={24}
              tickFormatter={(value) => data.find((point) => point.date === value)?.label ?? ""}
              tick={chartAxisTick}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              width={84}
              tick={chartAxisTick}
              tickFormatter={(value) => `$${Number(value).toLocaleString()}`}
              domain={["auto", "auto"]}
            />
            <Tooltip
              content={
                <ChartTooltip
                  labelFormatter={(value) =>
                    data.find((point) => point.date === value)?.label ?? value
                  }
                  valueFormatter={(v) => `$${v.toLocaleString()}`}
                />
              }
              allowEscapeViewBox={{ x: false, y: false }}
            />
            <ReferenceLine
              y={startingBalance}
              stroke={chartTheme.gridColor}
              strokeDasharray="4 4"
              label={{
                value: "Start",
                position: "right",
                fill: chartTheme.tickColor,
                fontSize: 9,
                fontFamily: chartTheme.fontFamily,
              }}
            />
            <Line
              type="monotone"
              dataKey="balance"
              stroke={balanceColor}
              strokeWidth={1.5}
              dot={false}
              name="Balance"
              activeDot={{ r: 4, fill: balanceColor, strokeWidth: 0 }}
              isAnimationActive={true}
              animationDuration={2000}
              animationEasing="ease-in-out"
            />
            <Line
              type="monotone"
              dataKey="equity"
              stroke={equityColor}
              strokeWidth={1.5}
              dot={false}
              name="Equity"
              activeDot={{ r: 4, fill: equityColor, strokeWidth: 0 }}
              isAnimationActive={true}
              animationDuration={2500}
              animationEasing="ease-in-out"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="flex items-center gap-5 mt-3 justify-center">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-[2px] rounded-full" style={{ backgroundColor: balanceColor }} />
          <span className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider">Balance</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-[2px] rounded-full" style={{ backgroundColor: equityColor }} />
          <span className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider">Equity</span>
        </div>
      </div>
    </div>
  );
}

export const mockEquityData: { date: string; label: string; balance: number; equity: number; drawdown: number }[] = [];
